import React, {useEffect, useMemo, useState} from "react";
import EChart from "../../components/charts/EChart.jsx";
import Tooltip from "../../components/Tooltip.jsx";

function legendConfiguration(option) {
    return Array.isArray(option?.legend) ? option.legend[0] : option?.legend;
}

function uniqueLegendItems(option) {
    const legend = legendConfiguration(option);
    const configuredItems = Array.isArray(legend?.data)
        ? legend.data.map((item) => typeof item === "string" ? item : item?.name)
        : [];
    const seriesItems = (option?.series ?? [])
        .map((series) => series?.name)
        .filter(Boolean);
    const pieItems = (option?.series ?? [])
        .filter((series) => series?.type === "pie")
        .flatMap((series) => (series?.data ?? []).map((item) => item?.name).filter(Boolean));
    const names = configuredItems.length > 0 ? configuredItems : [...seriesItems, ...pieItems];

    return [...new Set(names)].map((name, index) => {
        const seriesIndex = (option?.series ?? []).findIndex((series) => series?.name === name);
        const pieItem = (option?.series ?? [])
            .flatMap((series) => series?.type === "pie" ? (series?.data ?? []) : [])
            .find((item) => item?.name === name);
        const paletteIndex = seriesIndex >= 0 ? seriesIndex : index;

        return {
            name,
            color: pieItem?.itemStyle?.color
                ?? option?.series?.[seriesIndex]?.itemStyle?.color
                ?? option?.color?.[paletteIndex % Math.max(option?.color?.length ?? 0, 1)]
                ?? "#64748b",
            line: seriesIndex >= 0 && option?.series?.[seriesIndex]?.type === "line",
        };
    });
}

function formattedLegendName(name, formatter) {
    if (typeof formatter === "function") {
        return formatter(name);
    }

    if (typeof formatter === "string") {
        return formatter.replace("{name}", name);
    }

    return name;
}

function externalizedGrid(grid, legend) {
    const grids = Array.isArray(grid) ? grid : [grid];
    const externalized = grids.map((item) => {
        if (!item) {
            return item;
        }

        if (legend?.top !== undefined) {
            return {...item, top: 16, bottom: 12};
        }

        return {...item, bottom: 12};
    });

    return Array.isArray(grid) ? externalized : externalized[0];
}

function externalizedSeries(series) {
    return (series ?? []).map((item) => {
        if (item?.type !== "pie" || !Array.isArray(item.center)) {
            return item;
        }

        return {...item, center: [item.center[0], "50%"]};
    });
}

function externalizedChartGrid(option, legend) {
    if (option.grid) {
        return externalizedGrid(option.grid, legend);
    }

    if (option.xAxis || option.yAxis) {
        return {left: 12, right: 12, top: 16, bottom: 12, containLabel: true};
    }

    return undefined;
}

export default function ReportingEChart({option, className = "h-72", height, loading = false}) {
    const legend = legendConfiguration(option);
    const items = useMemo(() => uniqueLegendItems(option), [option]);
    const itemNamesKey = items.map((item) => item.name).join("\u0000");
    const [selected, setSelected] = useState({});

    useEffect(() => {
        setSelected((current) => Object.fromEntries(items.map((item) => [
            item.name,
            current[item.name] ?? legend?.selected?.[item.name] ?? true,
        ])));
    }, [itemNamesKey]);

    const chartOption = useMemo(() => {
        const normalizedOption = {
            ...option,
            grid: externalizedChartGrid(option, legend),
        };

        if (!legend || items.length === 0) {
            return normalizedOption;
        }

        const hiddenLegend = {...legend, show: false, selected};

        return {
            ...normalizedOption,
            legend: Array.isArray(option.legend) ? [hiddenLegend, ...option.legend.slice(1)] : hiddenLegend,
            series: externalizedSeries(option.series),
        };
    }, [option, legend, items.length, selected]);

    return <div className="w-full min-w-0">
        <EChart option={chartOption} className={className} height={height} loading={loading}/>
        {legend && items.length > 0 && <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-4 pb-2 pt-2">
            {items.map((item, index) => {
                const active = selected[item.name] !== false;
                const label = formattedLegendName(item.name, legend.formatter);

                return <Tooltip
                    key={`${item.name}-${index}`}
                    bg="dark"
                    size="md"
                    position="top"
                    className="inline-flex min-w-0 max-w-full"
                    triggerClassName={`inline-flex min-w-0 max-w-full items-center gap-2 text-sm font-medium ${active ? "text-slate-600 dark:text-slate-300" : "text-slate-400 line-through dark:text-slate-500"}`}
                    ariaLabel={item.name}
                    ariaPressed={active}
                    onClick={() => setSelected((current) => ({...current, [item.name]: !active}))}
                    trigger={<>
                        <span className="relative inline-flex h-3 w-4 shrink-0 items-center justify-center">
                            {item.line
                                ? <><span className="h-0.5 w-4" style={{backgroundColor: item.color}}/><span className="absolute h-2 w-2 rounded-full border-2 border-white dark:border-slate-900" style={{backgroundColor: item.color}}/></>
                                : <span className="h-2.5 w-4 rounded-sm" style={{backgroundColor: item.color}}/>}
                        </span>
                        <span className="max-w-64 truncate">{label}</span>
                    </>}
                >
                    <div className="text-xs leading-5 text-gray-200">{item.name}</div>
                </Tooltip>;
            })}
        </div>}
    </div>;
}
